import { Scanner, ScanSettings, ScanResult } from '@/services/ScannerService'
import { mapByTemplate } from '@utils/dto.mapper'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  startScannerService,
  discoverScanners,
  scanDocument,
  getScannerCapabilities,
} from '@state/thunks/ScannerThunk'
import { isObject } from 'lodash'
import { FileTypeEnum } from '@/common/interfaces'
import { SettingsValidation } from '@/common/ScannerSettingValidators'
import { rotateScannerImages } from '../thunks/ImagesThunk'

export interface ScannerState {
  scanners: Scanner[]
  selectedScannerId: string | null
  isDiscovering: boolean
  discoveryError: string | null

  isScanning: boolean
  scanProgress: number
  scanError: string | null
  currentScan: ScanResult | null

  scanSettings: ScanSettings
  serviceConnected: boolean
  loadScannerCaps: boolean
  scannerCaps: Scanner['capabilities']
  capsError: string | null
  isFullySupported: boolean

  selectedDocumentType: FileTypeEnum | null
  validatedSettings: ScanSettings | null
  capsWarnings: string[]
  isRotating: boolean
  rotationError: string | null
  rotatedImages: string[] | null
  failedRotationIndices: number[]
}

const initialState: ScannerState = {
  scanners: [],
  selectedScannerId: null,
  isDiscovering: false,
  discoveryError: null,

  isScanning: false,
  scanProgress: 0,
  scanError: null,
  currentScan: null,
  scanSettings: {
    deviceId: '',
    dpi: 300,
    colorMode: 'Color',
    format: 'pdf',
    paperSource: 'Feeder',
    pageSize: 'A4',
    skipBlankPages: false,
    blankThreshold: 0,
    compression: 75,
  },
  scannerCaps: {
    dpiValues: [],
    bitDepthValues: [],
    supportsFlatbed: false,
    supportsFeeder: false,
    supportsDuplex: false,
    driver: '',
    maxWidth: 0,
    maxHeight: 0,
    model: '',
  },

  capsError: null,
  loadScannerCaps: false,
  serviceConnected: false,
  isFullySupported: false,
  selectedDocumentType: null,
  validatedSettings: {
    dpi: 0,
    colorMode: 'Color',
    format: 'pdf',
    paperSource: 'Feeder',
    compression: 0,
  },
  capsWarnings: [],
  isRotating: false,
  rotationError: null,
  rotatedImages: null,
  failedRotationIndices: [],
}

const scannerSlice = createSlice({
  name: 'scanner',
  initialState,
  reducers: {
    selectScanner: (state, action: PayloadAction<string>) => {
      state.selectedScannerId = action.payload
      state.scanSettings.deviceId = action.payload
    },
    setScanProgress: (state, action: PayloadAction<number>) => {
      state.scanProgress = action.payload
    },
    setDocumentType: (state, action: PayloadAction<FileTypeEnum>) => {
      state.selectedDocumentType = action.payload
    },
    clearCurrentScan: (state) => {
      // Revoke blob URL to free memory
      if (state.currentScan && state.currentScan.url.startsWith('blob:')) {
        URL.revokeObjectURL(state.currentScan.url)
      }
      state.currentScan = null
      state.scanProgress = 0
      state.scanError = null
    },
    updateScanSettings: (
      state,
      action: PayloadAction<Partial<ScanSettings>>,
    ) => {
      state.scanSettings = {
        ...state.scanSettings,
        ...action.payload,
      }
    },
    resetScanSettings: (state) => {
      state.scanSettings = initialState.scanSettings
    },
    applyValidatedSettings: (state) => {
      if (state.validatedSettings) {
        state.scanSettings = {
          ...state.scanSettings,
          dpi: state.validatedSettings.dpi,
          colorMode: state.validatedSettings.colorMode,
          paperSource: state.validatedSettings.paperSource,
          skipBlankPages: state.validatedSettings.skipBlankPages,
        }
      }
    },
    setServiceStatus: (state, action: PayloadAction<boolean>) => {
      state.serviceConnected = action.payload
    },
    mergeValidatedSettings: (state, action) => {
      console.log('scanner/mergeValidatedSettings', action.payload)
      const validation: SettingsValidation = action.payload.validation
      const documentType = action.payload.fileType
      state.loadScannerCaps = true
      if (validation) {
        state.selectedDocumentType = documentType
        state.validatedSettings = validation.reducedSettings
        state.capsWarnings = validation.warnings
        state.isFullySupported = validation.isFullySupported

        state.scanSettings = {
          ...state.scanSettings,
          dpi: validation.reducedSettings.dpi,
          colorMode: validation.reducedSettings.colorMode,
          paperSource: validation.reducedSettings.paperSource,
          skipBlankPages: validation.reducedSettings.skipBlankPages,
        }
      }
    },
    clearError: (state) => {
      state.scanError = null
      state.discoveryError = null
    },
    resetScannerState: () => initialState,
    setCurrentScanAfterRotation: (state, action: PayloadAction<ScanResult>) => {
      if (state.currentScan && state.currentScan.url.startsWith('blob:')) {
        URL.revokeObjectURL(state.currentScan.url)
      }
      state.currentScan = action.payload
    },
    clearRotationState: (state) => {
      state.rotatedImages = null
      state.failedRotationIndices = []
      state.rotationError = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(startScannerService.fulfilled, (state, action) => {
      if (action.payload && Array.isArray(action.payload)) {
        state.scanners = action.payload as Scanner[]
        if (action.payload.length > 0) {
          state.selectedScannerId = action.payload[0].id
          state.scanSettings.deviceId = action.payload[0].id
        }
      }
    })

    builder
      .addCase(discoverScanners.pending, (state) => {
        state.isDiscovering = true
        state.discoveryError = null
      })
      .addCase(discoverScanners.fulfilled, (state, action) => {
        state.isDiscovering = false
        state.scanners = action.payload as Scanner[]
        state.discoveryError = null

        // Auto-select first scanner if none selected
        if (action.payload && Array.isArray(action.payload)) {
          if (action.payload.length > 0 && !state.selectedScannerId) {
            state.selectedScannerId = action.payload[0].id
            state.scanSettings.deviceId = action.payload[0].id
          }
        }
      })
      .addCase(discoverScanners.rejected, (state, action) => {
        state.isDiscovering = false
        state.discoveryError = action.payload as string
        state.scanners = []
      })
    builder
      .addCase(scanDocument.pending, (state) => {
        state.isScanning = true
        state.scanProgress = 0
        state.scanError = null
      })
      .addCase(scanDocument.fulfilled, (state, action) => {
        state.isScanning = false
        state.scanProgress = 100
        console.log('scan document action.payload : ', action.payload)
        state.currentScan = action.payload as ScanResult
        state.scanError = null
      })
      .addCase(scanDocument.rejected, (state, action) => {
        state.isScanning = false
        state.scanProgress = 0
        state.scanError = action.payload as string
      })
    builder
      .addCase(getScannerCapabilities.pending, (state) => {
        state.loadScannerCaps = false
      })
      .addCase(getScannerCapabilities.fulfilled, (state, action) => {
        state.loadScannerCaps = true
        if (action.payload && isObject(action.payload)) {
          state.scannerCaps = mapByTemplate(
            action.payload,
            initialState.scannerCaps,
          )
        }
      })
      .addCase(getScannerCapabilities.rejected, (state, action) => {
        state.capsError = action.payload as string
        state.loadScannerCaps = false
      })
    builder
      .addCase(rotateScannerImages.pending, (state) => {
        state.isRotating = true
        state.rotationError = null
      })
      .addCase(rotateScannerImages.fulfilled, (state, action) => {
        state.isRotating = false
        state.rotatedImages = action.payload.images
        state.failedRotationIndices = action.payload.failedIndices
        state.rotationError = null
      })
      .addCase(rotateScannerImages.rejected, (state, action) => {
        state.isRotating = false
        state.rotationError = action.payload as string
      })
  },
})

export const {
  setServiceStatus,
  selectScanner,
  updateScanSettings,
  clearCurrentScan,
  clearError,
  mergeValidatedSettings,
  setCurrentScanAfterRotation,
  clearRotationState,
} = scannerSlice.actions
export default scannerSlice.reducer
export const scannerReducer = scannerSlice.reducer
